import { useState, useRef, useEffect } from 'react';
import { useWakeLock } from '../hooks/useWakeLock';
import { PresenterAudioManager } from '../components/presenter/PresenterAudioManager';
import {
  calcHIITDuration,
  formatPhaseTime,
  advancePhase,
  calcOverallProgress,
} from './hiit.logic';
import './HIITPage.css';
import './TimerStopwatch.css';

// ── Presets (array so they can later be loaded from settings) ──────────────
const PRESETS = [
  { name: 'Blitz-Sprint',       work: 20, rest: 10, rounds:  8, intensity: 4, char: 'Kurz, explosiv – maximaler Output in minimaler Zeit' },
  { name: 'Power-Block',        work: 40, rest: 20, rounds:  6, intensity: 3, char: 'Ausgewogen – solide Intensität für Einsteiger & Fortgeschrittene' },
  { name: 'Ausdauer-Intervall', work: 45, rest: 15, rounds: 10, intensity: 5, char: 'Hohe Belastung – testet Kondition und mentale Stärke' },
  { name: 'Kraft-Pause',        work: 30, rest: 30, rounds: 10, intensity: 3, char: 'Gleichgewicht – ideal für kraftbetontes Intervalltraining' },
  { name: 'Einsteiger-Flow',    work: 30, rest: 45, rounds:  6, intensity: 2, char: 'Sanfter Einstieg – mehr Erholung, kontrollierter Rhythmus' },
  { name: 'Hardcore-Finish',    work: 50, rest: 10, rounds: 12, intensity: 5, char: 'Maximale Last – nur für erfahrene Athleten geeignet' },
];

// Color per intensity level (index = level 1–5)
const PIP_COLORS = ['', '#3B6D11', '#639922', '#EF9F27', '#f97316', '#E24B4A'];

function IntensityPips({ level }) {
  return (
    <div className="hiit-intensity-pips">
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className="hiit-pip"
          style={{ background: i <= level ? PIP_COLORS[level] : 'rgba(255,255,255,0.12)' }}
        />
      ))}
    </div>
  );
}

export default function HIITPage() {
  // ── Config state ──────────────────────────────────────────────────────────
  const [work,   setWork]   = useState(40);
  const [rest,   setRest]   = useState(20);
  const [rounds, setRounds] = useState(8);
  const [soundsEnabled, setSoundsEnabled] = useState(true);
  const [selectedPreset, setSelectedPreset] = useState(null);

  // ── Timer state ───────────────────────────────────────────────────────────
  const [view,     setView]     = useState('config'); // config|countdown|running|paused|done
  const [phase,    setPhase]    = useState('work');   // work|rest
  const [curRound, setCurRound] = useState(1);
  const [timeLeft, setTimeLeft] = useState(0);
  const [cdNum,    setCdNum]    = useState(3);
  // Incremented on skip-while-running to force the running useEffect to restart
  const [runKey,   setRunKey]   = useState(0);

  // Refs for interval closures (never stale)
  const intervalRef           = useRef(null);
  const timeLeftRef           = useRef(0);
  const phaseRef              = useRef('work');
  const curRoundRef           = useRef(1);
  const workRef               = useRef(40);
  const restRef               = useRef(20);
  const roundsRef             = useRef(8);
  const phaseEndTimeRef       = useRef(null);   // wall-clock end of current phase
  const lastBeepSecRef        = useRef(null);   // deduplicates beeps across 200ms ticks
  const boxCounterFiredRef    = useRef(false);  // fires box counter once per work phase
  const animFrameRef          = useRef(null);   // requestAnimationFrame ID for smooth bar
  const progressBarRef        = useRef(null);   // direct DOM ref — bypasses React for 60fps
  const pausedRemainingMsRef  = useRef(null);   // exact ms remaining when paused

  // Audio manager ref
  const audioManagerRef = useRef(null);
  if (!audioManagerRef.current) {
    audioManagerRef.current = new PresenterAudioManager();
  }
  const soundsEnabledRef = useRef(soundsEnabled);

  // Keep config refs in sync with state (used by countdown + tick closures)
  useEffect(() => { workRef.current   = work;   }, [work]);
  useEffect(() => { restRef.current   = rest;   }, [rest]);
  useEffect(() => { roundsRef.current = rounds; }, [rounds]);
  useEffect(() => { soundsEnabledRef.current = soundsEnabled; }, [soundsEnabled]);

  // Cleanup audio manager on unmount
  useEffect(() => () => audioManagerRef.current?.destroy(), []);

  // Wake lock while running
  useWakeLock(view === 'running');

  // ── Countdown effect ──────────────────────────────────────────────────────
  useEffect(() => {
    if (view !== 'countdown') return;
    let count = 3;
    setCdNum(3);
    if (soundsEnabledRef.current) audioManagerRef.current.playBeep();
    const id = setInterval(() => {
      count--;
      if (count > 0) {
        setCdNum(count);
        if (soundsEnabledRef.current) audioManagerRef.current.playBeep();
      } else {
        clearInterval(id);
        // Transition to running
        const w = workRef.current;
        phaseRef.current        = 'work';
        curRoundRef.current     = 1;
        timeLeftRef.current     = w;
        phaseEndTimeRef.current = Date.now() + w * 1000;
        lastBeepSecRef.current  = null;
        boxCounterFiredRef.current = false;
        setPhase('work');
        setCurRound(1);
        setTimeLeft(w);
        if (soundsEnabledRef.current) audioManagerRef.current.playStartExercise();
        setView('running');
      }
    }, 1000);
    return () => clearInterval(id);
  }, [view]);

  // ── Running timer effect (wall-clock 200ms polling — drift-immune on iOS) ──
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (view !== 'running') {
      clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      const remainingMs = phaseEndTimeRef.current - Date.now();
      if (remainingMs <= 0) {
        const result = advancePhase({
          phase:    phaseRef.current,
          curRound: curRoundRef.current,
          rounds:   roundsRef.current,
          work:     workRef.current,
          rest:     restRef.current,
        });
        if (result.done) {
          clearInterval(intervalRef.current);
          setView('done');
        } else {
          phaseRef.current        = result.phase;
          curRoundRef.current     = result.curRound;
          timeLeftRef.current     = result.timeLeft;
          phaseEndTimeRef.current = Date.now() + result.timeLeft * 1000;
          lastBeepSecRef.current  = null;
          boxCounterFiredRef.current = false;
          setPhase(result.phase);
          setCurRound(result.curRound);
          setTimeLeft(result.timeLeft);
          if (soundsEnabledRef.current) {
            if (result.phase === 'rest') audioManagerRef.current.playBreak();
            if (result.phase === 'work') audioManagerRef.current.playStartExercise();
          }
        }
      } else {
        const secs = Math.ceil(remainingMs / 1000);
        timeLeftRef.current = secs;
        setTimeLeft(secs);
        if (soundsEnabledRef.current) {
          // Rest: beep each second in the last 10s
          if (phaseRef.current === 'rest' && secs <= 10 && secs !== lastBeepSecRef.current) {
            lastBeepSecRef.current = secs;
            audioManagerRef.current.playBeep();
          }
          // Work: box counter once when ≤10s remain (like the Presenter)
          if (phaseRef.current === 'work' && secs <= 10 && !boxCounterFiredRef.current) {
            boxCounterFiredRef.current = true;
            audioManagerRef.current.playBoxCounter();
          }
        }
      }
    }, 200);
    return () => clearInterval(intervalRef.current);
  }, [view, runKey]); // runKey forces restart when skip is called while already running

  // ── Smooth progress bar via requestAnimationFrame (60fps, wall-clock exact) ──
  // CSS transitions can't be smooth when tick intervals vary on iOS — RAF reads
  // the wall clock every frame and sets style.width directly, bypassing React.
  useEffect(() => {
    if (view !== 'running') {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      return;
    }

    function rafTick() {
      if (!progressBarRef.current) return;
      const remainingMs = Math.max(0, phaseEndTimeRef.current - Date.now());
      const totalMs = (phaseRef.current === 'work' ? workRef.current : restRef.current) * 1000;
      const pct = totalMs > 0 ? Math.min(100, (remainingMs / totalMs) * 100) : 0;
      progressBarRef.current.style.width = `${pct}%`;
      animFrameRef.current = requestAnimationFrame(rafTick);
    }

    animFrameRef.current = requestAnimationFrame(rafTick);
    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
    };
  }, [view, runKey]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  function handleStart() {
    audioManagerRef.current.unlock();
    setView('countdown');
  }

  function handlePause() {
    pausedRemainingMsRef.current = Math.max(0, phaseEndTimeRef.current - Date.now());
    clearInterval(intervalRef.current);
    setView('paused');
  }

  function handleResume() {
    phaseEndTimeRef.current = Date.now() + (pausedRemainingMsRef.current ?? timeLeftRef.current * 1000);
    pausedRemainingMsRef.current = null;
    lastBeepSecRef.current  = null;
    setView('running');
  }

  function handleReset() {
    clearInterval(intervalRef.current);
    setView('countdown');
  }

  function handleSkip() {
    clearInterval(intervalRef.current);
    const result = advancePhase({
      phase:    phaseRef.current,
      curRound: curRoundRef.current,
      rounds:   roundsRef.current,
      work:     workRef.current,
      rest:     restRef.current,
    });
    if (result.done) {
      setView('done');
    } else {
      phaseRef.current        = result.phase;
      curRoundRef.current     = result.curRound;
      timeLeftRef.current     = result.timeLeft;
      phaseEndTimeRef.current = Date.now() + result.timeLeft * 1000;
      lastBeepSecRef.current  = null;
      boxCounterFiredRef.current = false;
      setPhase(result.phase);
      setCurRound(result.curRound);
      setTimeLeft(result.timeLeft);
      if (soundsEnabledRef.current) {
        if (result.phase === 'rest') audioManagerRef.current.playBreak();
        if (result.phase === 'work') audioManagerRef.current.playStartExercise();
      }
      if (view === 'running') {
        // view is already 'running' → setView('running') would be a no-op and
        // the useEffect wouldn't re-run. Increment runKey to force a restart.
        setRunKey((k) => k + 1);
      } else {
        setView('running');
      }
    }
  }

  function handleAbort() {
    clearInterval(intervalRef.current);
    setView('config');
  }

  function handlePresetSelect(i) {
    const p = PRESETS[i];
    workRef.current   = p.work;
    restRef.current   = p.rest;
    roundsRef.current = p.rounds;
    setWork(p.work);
    setRest(p.rest);
    setRounds(p.rounds);
    setSelectedPreset(i);
  }

  // ── Derived values for timer screen ───────────────────────────────────────
  const isTimerMode = view !== 'config';
  const timerBg =
    view === 'running' || view === 'paused'
      ? phase === 'work' ? '#0d2b1a' : '#0d1e33'
      : '#1a2332';
  const phaseColor  = phase === 'work' ? '#4ade80' : '#60a5fa';
  const overallPct  = calcOverallProgress(phase, curRound, work, rest, rounds, timeLeft);
  const [timerMins, timerSecs] = formatPhaseTime(timeLeft).split(':');

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className={isTimerMode ? 'hiit-fullscreen' : undefined}
      style={isTimerMode ? { background: timerBg } : undefined}
      data-theme={isTimerMode ? 'dark' : undefined}
      data-bs-theme={isTimerMode ? 'dark' : undefined}
    >

      {/* ── CONFIG ─────────────────────────────────────────────────────── */}
      {view === 'config' && (
        <>
          <h4 className="mb-4" style={{ color: 'var(--color-text)', fontWeight: 700 }}>HIIT Quick Start</h4>

          {/* Slider card */}
          <div className="tools-card mb-4">
            <div className="hiit-section-label">Freie Einstellung</div>

            {[
              { label: 'Belastungszeit', value: work,   min: 10, max: 120, step: 5, unit: 's',
                onInput: (v) => { workRef.current = v;   setWork(v);   setSelectedPreset(null); } },
              { label: 'Pausenzeit',     value: rest,   min: 5,  max: 90,  step: 5, unit: 's',
                onInput: (v) => { restRef.current = v;   setRest(v);   setSelectedPreset(null); } },
              { label: 'Runden',         value: rounds, min: 2,  max: 20,  step: 1, unit: '',
                onInput: (v) => { roundsRef.current = v; setRounds(v); setSelectedPreset(null); } },
            ].map(({ label, value, min, max, step, unit, onInput }) => (
              <div key={label} className="hiit-slider-row">
                <span className="hiit-slider-label">{label}</span>
                <input
                  type="range"
                  className="hiit-slider"
                  min={min}
                  max={max}
                  step={step}
                  value={value}
                  onChange={(e) => onInput(Number(e.target.value))}
                />
                <span className="hiit-slider-value">{value}{unit}</span>
              </div>
            ))}

            <div className="hiit-summary-chips">
              {[
                { label: 'Gesamtdauer', val: `${calcHIITDuration(work, rest, rounds)} min` },
                { label: 'Runden',      val: rounds },
                { label: 'Work',        val: `${work}s` },
                { label: 'Rest',        val: `${rest}s` },
              ].map(({ label, val }) => (
                <div key={label} className="hiit-sum-chip">
                  <div className="hiit-sum-chip-label">{label}</div>
                  <div className="hiit-sum-chip-val">{val}</div>
                </div>
              ))}
            </div>

            <div className="form-check mt-3 mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="hiit_sounds_enabled"
                checked={soundsEnabled}
                onChange={(e) => setSoundsEnabled(e.target.checked)}
              />
              <label className="form-check-label hiit-sounds-label" htmlFor="hiit_sounds_enabled">
                <i className="bi bi-volume-up me-1" />
                Töne aktivieren (Beep &amp; Übergänge)
              </label>
            </div>

            <button
              className="workout-control-btn-fullwidth"
              onClick={handleStart}
            >
              <i className="bi bi-play-fill" />
              Workout starten
            </button>
          </div>

          {/* Preset grid */}
          <div className="tools-card">
          <div className="hiit-section-label">1-Klick Presets</div>
          <div className="hiit-presets-grid">
            {PRESETS.map((p, i) => (
              <div
                key={p.name}
                className={`hiit-preset-card${selectedPreset === i ? ' selected' : ''}`}
                onClick={() => handlePresetSelect(i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handlePresetSelect(i)}
              >
                <div className="hiit-preset-name">{p.name}</div>
                <div className="hiit-preset-stats">
                  {p.work}s Work · {p.rest}s Rest · {p.rounds} Runden
                </div>
                <div className="hiit-preset-char">{p.char}</div>
                <div className="hiit-preset-footer">
                  <span className="hiit-preset-dur">
                    ca. {calcHIITDuration(p.work, p.rest, p.rounds)} min
                  </span>
                  <IntensityPips level={p.intensity} />
                </div>
              </div>
            ))}
          </div>
          </div>
        </>
      )}

      {/* ── COUNTDOWN ──────────────────────────────────────────────────── */}
      {view === 'countdown' && (
        <>
          <div className="workout-control-topbar">
            <div className="workout-control-topbar__slot">
              <button
                className="workout-control-btn-icon-sm"
                onClick={handleAbort}
                aria-label="Workout abbrechen"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="workout-control-topbar__slot workout-control-topbar__slot--center"></div>
            <div className="workout-control-topbar__slot"></div>
          </div>
          <div className="workout-control-content">
            <div className="hiit-countdown">
              <div className="hiit-countdown-label">Bereit machen</div>
              <div className="hiit-countdown-num">{cdNum}</div>
              <div className="hiit-countdown-cfg">
                {work}s Work · {rest}s Rest · {rounds} Runden
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── RUNNING / PAUSED ───────────────────────────────────────────── */}
      {(view === 'running' || view === 'paused') && (
        <>
          <div className="workout-control-topbar">
            <div className="workout-control-topbar__slot">
              <button
                className="workout-control-btn-icon-sm"
                onClick={handleAbort}
                aria-label="Workout abbrechen"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="workout-control-topbar__slot workout-control-topbar__slot--center">
              <span className={`workout-control-phase-pill workout-control-phase-pill--${
                phase === 'work' ? 'work' : 'rest'
              }`}>
                {phase === 'work' ? 'ARBEIT' : 'PAUSE'}
              </span>
            </div>
            <div className="workout-control-topbar__slot"></div>
          </div>

          <div className="workout-control-content">
            <div className="hiit-timer">
              <div
                className="hiit-phase-badge"
                style={{
                  background: phase === 'work'
                    ? 'rgba(74,222,128,0.15)'
                    : 'rgba(96,165,250,0.15)',
                  color: phaseColor,
                }}
              >
                {phase === 'work' ? 'WORK' : 'REST'}
              </div>

              <div className="hiit-round-info">Runde {curRound} von {rounds}</div>

              <div className="hiit-timer-display" style={{ color: phaseColor }}>
                <span className="hiit-timer-segment">{timerMins}</span>
                <span className="hiit-timer-colon" style={{ color: phaseColor }}>:</span>
                <span className="hiit-timer-segment">{timerSecs}</span>
              </div>

              <div
                className="hiit-phase-label"
                style={{ color: phase === 'work' ? 'rgba(74,222,128,0.55)' : 'rgba(96,165,250,0.55)' }}
              >
                {phase === 'work' ? 'Belastung' : 'Erholung'}
              </div>

              <div className="hiit-progress-wrap">
                <div className="hiit-progress-track">
                  <div
                    className="hiit-progress-fill"
                    ref={progressBarRef}
                    style={{ background: phaseColor }}
                    // width is set directly by requestAnimationFrame (60fps, wall-clock exact)
                  />
                </div>
                <div className="hiit-progress-labels">
                  <span>{phase === 'work' ? 'Belastungsphase' : 'Erholungsphase'}</span>
                  <span>Gesamt {overallPct}%</span>
                </div>
              </div>

              <div className="hiit-rounds-dots">
                {Array.from({ length: rounds }, (_, i) => (
                  <span
                    key={i}
                    className="hiit-rdot"
                    style={{
                      background:
                        i + 1 < curRound  ? '#4ade80'
                        : i + 1 === curRound ? 'var(--color-accent)'
                        : 'rgba(255,255,255,0.15)',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="workout-control-bottombar">
            <button
              className="workout-control-btn-secondary"
              onClick={handleReset}
              aria-label="Reset"
            >
              <i className="bi bi-arrow-counterclockwise" />
            </button>
            <button
              className={`workout-control-btn-primary ${view === 'paused' ? 'is-paused' : 'is-playing'}`}
              onClick={view === 'paused' ? handleResume : handlePause}
              aria-label={view === 'paused' ? 'Weiter' : 'Pause'}
            >
              <span className="workout-control-btn-primary__icon workout-control-btn-primary__icon--pause">
                <i className="bi bi-pause-fill" />
              </span>
              <span className="workout-control-btn-primary__icon workout-control-btn-primary__icon--play">
                <i className="bi bi-play-fill" />
              </span>
            </button>
            <button
              className="workout-control-btn-secondary"
              onClick={handleSkip}
              aria-label="Überspringen"
            >
              <i className="bi bi-skip-end-fill" />
            </button>
          </div>
        </>
      )}

      {/* ── DONE ───────────────────────────────────────────────────────── */}
      {view === 'done' && (
        <div className="hiit-done">
          <div className="hiit-done-icon">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20,6 9,17 4,12" />
            </svg>
          </div>
          <div className="hiit-done-title">Workout abgeschlossen!</div>
          <div className="hiit-done-sub">Gut gemacht</div>
          <div className="hiit-done-stats">
            {[
              { val: `${calcHIITDuration(work, rest, rounds)} min`, label: 'Dauer' },
              { val: rounds,    label: 'Runden' },
              { val: `${work}s`, label: 'Work' },
              { val: `${rest}s`, label: 'Rest' },
            ].map(({ val, label }) => (
              <div key={label} className="hiit-done-stat">
                <div className="hiit-done-stat-val">{val}</div>
                <div className="hiit-done-stat-label">{label}</div>
              </div>
            ))}
          </div>
          <div className="d-flex flex-column gap-3" style={{ maxWidth: 480, margin: '0 auto', padding: '0 16px' }}>
            <button className="workout-control-btn-fullwidth" onClick={handleReset}>
              <i className="bi bi-arrow-clockwise" />
              Erneut starten
            </button>
            <button className="workout-control-btn-fullwidth workout-control-btn-fullwidth--ghost" onClick={handleAbort}>
              Zur Übersicht
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
